import React, {createContext, Dispatch, SetStateAction, useEffect, useState} from 'react';
import moment from 'moment';
import {InstalmentPlan} from '../../types/InstalmentPlan/InstalmentPlan';
import InstalmentPlanService from '../../services/InstalmentPlanService';
import PaginationProvider from '../../components/table/PaginationContext';
import {StateFilter} from '../../components/filter/GenericTableFilters';
import {PageMetadata} from '../../types/Page';
import {Filter} from '../../types/Filter';

export const InstalmentPlanListDataContext = createContext<{
  instalmentPlans: InstalmentPlan[];
  filters: {
    stateFilter: StateFilter[];
    setStateFilter: Dispatch<SetStateAction<StateFilter[]>>;
    dateStartFilter: string;
    setDateStartFilter: Dispatch<SetStateAction<string>>;
    dateEndFilter: string;
    setDateEndFilter: Dispatch<SetStateAction<string>>;
    searchFilter: string;
    setSearchFilter: Dispatch<SetStateAction<string>>;
    used: boolean;
  } | null;
  page: PageMetadata | undefined;
  reload: () => void;
  loading: boolean;
  isPrefilteredForCustomer: boolean;
  currentPage: { set: (page: number) => void; get: number };
  pageSize: { set: (size: number) => void; get: number };
}>({
  instalmentPlans: [] as InstalmentPlan[],
  filters: null,
  page: {} as PageMetadata,
  reload: () => {},
  loading: true,
  isPrefilteredForCustomer: false,
  currentPage: {
    set: (page) => {},
    get: 0,
  },
  pageSize: {
    set: (size) => {},
    get: 0,
  },
});

type InstalmentPlanListDataContextProviderProps = {
  pageSizeOptions: number[];
  children: any;
  customerId?: string;
  setInstalmentPlanCount?: (instalmentPlanCount: number) => void;
  hasPaginationProvider?: boolean;
};

const InstalmentPlanListDataContextProvider = ({
  pageSizeOptions,
  customerId,
  children,
  setInstalmentPlanCount,
  hasPaginationProvider = true,
}: InstalmentPlanListDataContextProviderProps) => {
  const [instalmentPlanList, setInstalmentPlanList] = useState<InstalmentPlan[]>([] as InstalmentPlan[]);
  const [pageMetaData, setPageMetaData] = useState<PageMetadata>();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [stateFilter, setStateFilter] = useState<StateFilter[]>([] as StateFilter[]);
  const [dateStartFilter, setDateStartFilter] = useState<string>('');
  const [dateEndFilter, setDateEndFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [filtersUsed, setFiltersUsed] = useState(false);

  const isInitialized = React.useRef(false);

  useEffect(() => {
    if (isInitialized.current) {
      reload();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (isInitialized.current) {
      if (currentPage !== 0) {
        setCurrentPage(0);
      } else {
        reload();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter, dateStartFilter, dateEndFilter, searchFilter, pageSize]);

  useEffect(() => {
    reload();
    isInitialized.current = true;
    return () => {
      isInitialized.current = false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = () => {
    setLoading(true);
    let filter: Filter = {
      customerRef: customerId,
    };
    if (!!stateFilter && stateFilter.length !== 0) {
      filter.statuses = Array.from(stateFilter.values()).join(',');
    }
    filter.search = searchFilter;
    filter.startDate = dateStartFilter && moment(dateStartFilter).format('YYYY-MM-DDT23:59:59.999') + 'Z';
    filter.endDate = dateEndFilter && moment(dateEndFilter).format('YYYY-MM-DDT23:59:59.999') + 'Z';

    setFiltersUsed((!!stateFilter && stateFilter.length !== 0) || !!dateStartFilter || !!dateEndFilter || !!searchFilter);

    InstalmentPlanService.getInstalmentPlans(pageSize, currentPage, 'desc', filter).then(({ results, page }) => {
      setInstalmentPlanCount && setInstalmentPlanCount(page.totalElements);
      setInstalmentPlanList(results);
      setPageMetaData(page);
      setLoading(false);
    });
  };

  return (
    <InstalmentPlanListDataContext.Provider
      value={{
        instalmentPlans: instalmentPlanList,
        filters: {
          stateFilter: stateFilter,
          setStateFilter: setStateFilter,
          dateStartFilter: dateStartFilter,
          setDateStartFilter: setDateStartFilter,
          dateEndFilter: dateEndFilter,
          setDateEndFilter: setDateEndFilter,
          searchFilter: searchFilter,
          setSearchFilter: setSearchFilter,
          used: filtersUsed,
        },
        page: pageMetaData,
        reload: reload,
        loading: loading,
        isPrefilteredForCustomer: !!customerId,
        currentPage: { set: setCurrentPage, get: currentPage },
        pageSize: { set: setPageSize, get: pageSize },
      }}
    >
      {hasPaginationProvider ? (
        <PaginationProvider
          pageSizeOptions={pageSizeOptions}
          currentPage={currentPage || 0}
          totalPages={pageMetaData?.totalPages || 0}
          pageSize={pageSize}
          setCurrentPage={setCurrentPage}
          changePageSize={setPageSize}
        >
          {children}
        </PaginationProvider>
      ) : (
        children
      )}
    </InstalmentPlanListDataContext.Provider>
  );
};

export default InstalmentPlanListDataContextProvider;
